import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DrawerV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { ISociety } from '@bloomays-lib/types.shared';
import {
  UPSERT_SOCIETY,
  UPDATE_MISSION,
  LINK_SOCIETY_TO_MISSION,
  UNLINK_SOCIETY_TO_MISSION,
  SEARCH_SOCIETY,
} from '@bloomays-lib/adapter.api-bloomer';

import { cancel, notify } from '../../../../helpers/toastify';
import { FieldV2, InputV2, SelectV2, Row2, InfoV2, DividerV2, CheckRow, SectionHeading } from '../formFields';

export type EditClientSocietyDrawerProps = {
  open: boolean;
  onClose: () => void;
  clientSociety: ISociety;
  clientBillingSociety?: ISociety | null;
  missionRecordId: string;
  contractType?: string | null;
  /** 'client' (société cliente, défaut) ou 'billing' (société de facturation rattachée). */
  variant?: 'client' | 'billing';
};

const SOCIETY_STATUS_OPTIONS = [
  { value: 'EI', label: '🇫🇷 Entreprise Individuelle / Micro-entreprise' },
  { value: 'EURL', label: '🇫🇷 EURL' },
  { value: 'SARL', label: '🇫🇷 SARL' },
  { value: 'SAS', label: '🇫🇷 SAS' },
  { value: 'SASU', label: '🇫🇷 SASU' },
  { value: 'Autre', label: '🇫🇷 Autre forme française' },
  { value: 'SUE', label: '🇪🇺 Société Union Européenne' },
  { value: 'SHUE', label: '🌍 Société hors Union Européenne' },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: 'bloomaysContract', label: 'Contrat Bloomays' },
  { value: 'devis', label: 'Devis' },
  { value: 'BDC', label: 'Bon de commande' },
  { value: 'clientContract', label: 'Contrat client' },
];

/**
 * Gestion de la société cliente en panneau latéral (DrawerV2) — édition via `UPSERT_SOCIETY`
 * (identité, adresse, délai de paiement, règles de facturation) + type de contrat de la mission
 * via `UPDATE_MISSION`. La société de facturation rattachée est affichée pour contexte.
 */
export const EditClientSocietyDrawer = ({
  open,
  onClose,
  clientSociety,
  clientBillingSociety,
  missionRecordId,
  contractType: initialContractType,
  variant = 'client',
}: EditClientSocietyDrawerProps) => {
  const { t } = useTranslation(['notify']);
  const isBilling = variant === 'billing';

  const [name, setName] = useState(clientSociety.name ?? '');
  const [status, setStatus] = useState(clientSociety.status ?? '');
  const [siret, setSiret] = useState(clientSociety.siret ?? '');
  const [tva, setTva] = useState(clientSociety.tva ?? '');
  const [addressPart1, setAddressPart1] = useState(clientSociety.addressPart1 ?? '');
  const [addressZip, setAddressZip] = useState(clientSociety.addressZip ?? '');
  const [addressTown, setAddressTown] = useState(clientSociety.addressTown ?? '');
  const [paydateDays, setPaydateDays] = useState<string>(
    clientSociety.paydateDays != null ? String(clientSociety.paydateDays) : '',
  );
  const [autoValidateInvoice, setAutoValidateInvoice] = useState(!!clientSociety.autoValidateInvoice);
  const [orderNumberRequired, setOrderNumberRequired] = useState(!!clientSociety.orderNumberRequired);
  const [contractType, setContractType] = useState(initialContractType ?? '');

  const [upsertSociety, { loading: savingSociety }] = useMutation(UPSERT_SOCIETY);
  const [updateMission, { loading: savingMission }] = useMutation(UPDATE_MISSION);
  const [linkSociety, { loading: linking }] = useMutation(LINK_SOCIETY_TO_MISSION);
  const [unlinkSociety, { loading: unlinking }] = useMutation(UNLINK_SOCIETY_TO_MISSION);

  const distinctBilling = clientBillingSociety && clientBillingSociety.recordId !== clientSociety.recordId;

  // Recherche / création d'une société de facturation (item 6).
  const [billingMode, setBillingMode] = useState<'search' | 'create'>('search');
  const [billingQuery, setBillingQuery] = useState('');
  const [billingName, setBillingName] = useState('');
  const [billingStatus, setBillingStatus] = useState('');
  const [billingSiret, setBillingSiret] = useState('');
  const [billingTva, setBillingTva] = useState('');

  const [searchSocieties, { data: societiesData, loading: loadingSocieties }] = useLazyQuery(SEARCH_SOCIETY);
  // Recherche société cliente à la frappe (nom ou SIRET, serveur) — debounce 300 ms, min 2 caractères.
  useEffect(() => {
    if (isBilling || distinctBilling || billingMode !== 'search') return;
    const q = billingQuery.trim();
    if (q.length < 2) return;
    const handle = setTimeout(() => void searchSocieties({ variables: { query: q, isClient: true } }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingQuery, billingMode, isBilling, distinctBilling]);

  const billingTooShort = billingQuery.trim().length < 2;
  const billingResults = ((societiesData?.searchSociety ?? []) as { recordId: string; name?: string; siret?: string }[])
    .filter((soc) => soc && soc.recordId && soc.recordId !== clientSociety.recordId)
    .slice(0, 8);

  const attachExistingBilling = async (societyId: string) => {
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await linkSociety({ variables: { societyId, missionId: missionRecordId, role: 'clientBilling' } });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const detachBilling = async () => {
    if (!clientBillingSociety?.recordId) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await unlinkSociety({
        variables: { societyId: clientBillingSociety.recordId, missionId: missionRecordId, role: 'clientBilling' },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const createBilling = async () => {
    if (!billingName.trim()) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      const res = await upsertSociety({
        variables: {
          input: {
            isClient: true,
            name: billingName,
            status: billingStatus || undefined,
            siret: billingSiret || undefined,
            tva: billingTva || undefined,
          },
        },
      });
      const newId = res.data?.upsertSociety?.recordId as string | undefined;
      if (!newId) throw new Error('Création de la société impossible');
      await linkSociety({ variables: { societyId: newId, missionId: missionRecordId, role: 'clientBilling' } });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const save = async () => {
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertSociety({
        variables: {
          input: {
            recordId: clientSociety.recordId,
            isClient: true,
            name,
            status: status || undefined,
            siret,
            tva: tva || undefined,
            addressPart1: addressPart1 || undefined,
            addressZip: addressZip || undefined,
            addressTown: addressTown || undefined,
            paydateDays: paydateDays ? Number(paydateDays) : undefined,
            autoValidateInvoice,
            orderNumberRequired,
          },
        },
      });
      if (!isBilling && contractType && contractType !== (initialContractType ?? '')) {
        await updateMission({ variables: { input: { recordId: missionRecordId, contractType } } });
      }
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={isBilling ? 'Société de facturation' : 'Société cliente'}
      subtitle={clientSociety.name}
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            Annuler
          </ButtonV2>
          <ButtonV2
            onClick={save}
            disabled={!name.trim()}
            loading={savingSociety || savingMission}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            Enregistrer
          </ButtonV2>
        </>
      }
    >
      <FieldV2 label="Nom" required>
        <InputV2 value={name} onChange={setName} placeholder="Raison sociale" />
      </FieldV2>
      <Row2>
        <FieldV2 label="Statut juridique">
          <SelectV2 value={status} onChange={setStatus} options={SOCIETY_STATUS_OPTIONS} placeholder="Choisir…" />
        </FieldV2>
        <FieldV2 label="SIRET">
          <InputV2 value={siret} onChange={setSiret} />
        </FieldV2>
      </Row2>
      <FieldV2 label="N° TVA">
        <InputV2 value={tva} onChange={setTva} placeholder="Optionnel" />
      </FieldV2>

      {!isBilling ? (
        <FieldV2 label="Type de contrat" hint="Type de contrat appliqué à la mission.">
          <SelectV2
            value={contractType}
            onChange={setContractType}
            options={CONTRACT_TYPE_OPTIONS}
            placeholder="Choisir…"
          />
        </FieldV2>
      ) : null}

      <FieldV2 label="Adresse">
        <InputV2 value={addressPart1} onChange={setAddressPart1} placeholder="N° et rue" />
      </FieldV2>
      <Row2>
        <FieldV2 label="Code postal">
          <InputV2 value={addressZip} onChange={setAddressZip} />
        </FieldV2>
        <FieldV2 label="Ville">
          <InputV2 value={addressTown} onChange={setAddressTown} />
        </FieldV2>
      </Row2>
      <FieldV2 label="Délai de paiement (j)">
        <InputV2 type="number" value={paydateDays} onChange={setPaydateDays} />
      </FieldV2>

      <CheckRow checked={autoValidateInvoice} onChange={setAutoValidateInvoice}>
        Validation automatique des factures
      </CheckRow>
      <CheckRow checked={orderNumberRequired} onChange={setOrderNumberRequired}>
        Numéro de commande (BDC) requis
      </CheckRow>

      {!isBilling ? (
        <>
          <DividerV2 />
          <SectionHeading>Société de facturation</SectionHeading>
          {distinctBilling ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '11px 13px',
                border: `1px solid ${dsV2.color.border}`,
                borderRadius: dsV2.radius.sm,
                background: dsV2.color.surface2,
              }}
            >
              <ReceiptLongRoundedIcon sx={{ fontSize: 18, color: dsV2.color.t3 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>
                  {clientBillingSociety?.name}
                  {clientBillingSociety?.status ? ` · ${clientBillingSociety.status}` : ''}
                </Box>
                <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>Entité de facturation distincte</Box>
              </Box>
              <ButtonV2
                variant="danger"
                size="small"
                onClick={detachBilling}
                loading={unlinking}
                startIcon={<LinkOffRoundedIcon sx={{ fontSize: 14 }} />}
              >
                Détacher
              </ButtonV2>
            </Box>
          ) : (
            <>
              <InfoV2 tone="blue">
                Aucune société de facturation distincte. Recherchez-en une existante ou créez-en une.
              </InfoV2>
              {/* Bascule Rechercher / Créer (motif ContactAddDrawer) */}
              <Box sx={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {(['search', 'create'] as const).map((mode) => {
                  const active = billingMode === mode;
                  return (
                    <Box
                      key={mode}
                      component="button"
                      type="button"
                      onClick={() => setBillingMode(mode)}
                      sx={{
                        flex: 1,
                        padding: '7px 10px',
                        borderRadius: dsV2.radius.sm,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: `1px solid ${active ? dsV2.color.bleu : dsV2.color.border}`,
                        background: active ? dsV2.color.bleuDim : dsV2.color.blanc,
                        color: active ? dsV2.color.bleu : dsV2.color.t3,
                      }}
                    >
                      {mode === 'search' ? 'Rechercher' : 'Créer'}
                    </Box>
                  );
                })}
              </Box>

              {billingMode === 'search' ? (
                <>
                  <FieldV2 label="Rechercher une société cliente" hint="Par nom ou SIRET.">
                    <InputV2 value={billingQuery} onChange={setBillingQuery} placeholder="Nom ou SIRET…" />
                  </FieldV2>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: 240, overflowY: 'auto' }}>
                    {billingTooShort ? (
                      <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
                        Saisissez au moins 2 caractères (nom ou SIRET).
                      </Box>
                    ) : loadingSocieties ? (
                      <Box sx={{ fontSize: 12, color: dsV2.color.t4, padding: '8px 0' }}>Recherche…</Box>
                    ) : billingResults.length === 0 ? (
                      <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
                        Aucune société trouvée — basculez sur « Créer ».
                      </Box>
                    ) : (
                      billingResults.map((soc) => (
                        <Box
                          key={soc.recordId}
                          role="button"
                          onClick={() => attachExistingBilling(soc.recordId)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                            padding: '9px 11px',
                            cursor: 'pointer',
                            borderRadius: dsV2.radius.sm,
                            border: `1px solid ${dsV2.color.border}`,
                            background: dsV2.color.blanc,
                            '&:hover': { borderColor: dsV2.color.bleu, background: dsV2.color.bleuDim },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>{soc.name}</Box>
                            {soc.siret ? (
                              <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>SIRET {soc.siret}</Box>
                            ) : null}
                          </Box>
                          <ButtonV2 variant="secondary" size="small" loading={linking} disabled={linking}>
                            Rattacher
                          </ButtonV2>
                        </Box>
                      ))
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <FieldV2 label="Nom" required>
                    <InputV2 value={billingName} onChange={setBillingName} placeholder="Raison sociale" />
                  </FieldV2>
                  <Row2>
                    <FieldV2 label="Statut juridique">
                      <SelectV2
                        value={billingStatus}
                        onChange={setBillingStatus}
                        options={SOCIETY_STATUS_OPTIONS}
                        placeholder="Choisir…"
                      />
                    </FieldV2>
                    <FieldV2 label="SIRET">
                      <InputV2 value={billingSiret} onChange={setBillingSiret} />
                    </FieldV2>
                  </Row2>
                  <FieldV2 label="N° TVA">
                    <InputV2 value={billingTva} onChange={setBillingTva} placeholder="Optionnel" />
                  </FieldV2>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonV2
                      size="small"
                      onClick={createBilling}
                      disabled={!billingName.trim()}
                      loading={savingSociety || linking}
                      startIcon={<SaveRoundedIcon sx={{ fontSize: 14 }} />}
                    >
                      Créer & rattacher
                    </ButtonV2>
                  </Box>
                </>
              )}
            </>
          )}
        </>
      ) : null}
    </DrawerV2>
  );
};

export default EditClientSocietyDrawer;
